import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceReviewerStrategiesStrategyEnumValues,
} from 'generated/resources/datatable.graphql'
import {
  PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment,
  PerformanceScoreConfigurationFragment,
  PerformanceTemplateQuestionnaireByReviewerStrategyFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'

import SimpleChip from 'modules/performance_process/components/overview/Targets/SimpleChip'
import tabsPicker from 'modules/performance_process/components/review/show/helpers/tabsPicker'
import { WeightTag } from 'modules/performance_process/components/score_configuration_weights/WeightTag'
import {
  configurationByStrategy,
  useStrategyWeights,
} from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import {
  QuestionnaireContent as QuestionnaireContentForStrategies,
  competenciesPlaceholderForStrategy,
  employeePotentialScoreForStrategy,
  employeeScoreForStrategy,
  getQuestionnaireTranslations,
  mapQuestion,
  questionsForStrategy,
} from 'modules/performance_process/lib/questionnaireForStrategies'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'
import EmptyState from 'modules/questionnaires/components/Questionnaire/EmptyState'

import { Questionnaire } from './Questionnaire'

type AllowedQuestionnaires =
  | PerformanceTemplateQuestionnaireByReviewerStrategyFragment
  | PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment
type Props = {
  reviewType: PerformanceReviewerStrategiesStrategyEnum[]
  questionnaires?: AllowedQuestionnaires
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
}

export const QuestionnairePreview: React.FC<Props> = ({
  reviewType,
  questionnaires,
  scoreConfiguration,
}) => {
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()
  const tabs = tabsPicker(reviewType)
  const [currentTab, setCurrentTab] = React.useState<PerformanceReviewerStrategiesStrategyEnum>(
    tabs[0]?.id || 'self'
  )

  const { isSectionWeightVisible, weightsBySectionId } = useStrategyWeights({
    strategy: currentTab,
    scoreConfiguration,
  })

  const handleChipClick = (id: PerformanceReviewerStrategiesStrategyEnum) => {
    if (isEnumValue(PerformanceReviewerStrategiesStrategyEnumValues, id)) {
      setCurrentTab(id)
    }
  }

  const currentQuestionnaire = questionnaires
    ? questionsForStrategy(questionnaires, currentTab)
    : []

  const currentEmployeeScoreQuestionnaire = questionnaires
    ? employeeScoreForStrategy(questionnaires, currentTab)
    : []

  const currentEmployeePotentialScoreQuestionnaire = questionnaires
    ? employeePotentialScoreForStrategy(questionnaires, currentTab)
    : []

  const currentCompetenciesPlaceholderQuestionnaire = questionnaires
    ? competenciesPlaceholderForStrategy(questionnaires, currentTab)
    : []

  const mapContent = (content: QuestionnaireContentForStrategies[], isEmployeeScore = false) =>
    content
      .map((item) => {
        if (item.type === 'section') {
          return {
            uuid: item.uuid,
            title: item.sectionTitle || '',
            questions: item.questions.map((q) => ({
              ...mapQuestion(q),
            })),
            isEmployeeScore: isEmployeeScore,
          }
        }

        const firstQuestion = item.questions[0]
        if (!firstQuestion) {
          return null
        }
        return {
          ...mapQuestion(firstQuestion),
          isEmployeeScore: isEmployeeScore,
          uuid: firstQuestion.uuid,
        }
      })
      .filter(Boolean)

  const questionnaireContent = mapContent(currentQuestionnaire)
  const employeeScoreContent = mapContent(currentEmployeeScoreQuestionnaire, true)
  const employeePotentialScoreContent = mapContent(currentEmployeePotentialScoreQuestionnaire, true)
  const competenciesPlaceholderContent = mapContent(
    currentCompetenciesPlaceholderQuestionnaire,
    true
  )

  const showEmptyState =
    !questionnaireContent.length &&
    !employeeScoreContent.length &&
    !employeePotentialScoreContent.length &&
    !competenciesPlaceholderContent.length

  const renderSectionHeader = (sectionId: string) => {
    if (isSectionWeightVisible) {
      return <WeightTag weight={weightsBySectionId[sectionId] ?? 0} weightable />
    }
  }

  const strategyConfigurations = configurationByStrategy(scoreConfiguration)
  const { hasCustomWeights } = useStrategyWeights({
    strategy: currentTab,
    scoreConfiguration,
  })

  const getTabTag = (tab: PerformanceReviewerStrategiesStrategyEnum) => {
    if (!hasCustomScoreConfigurationEnabled || !hasCustomWeights) {
      return null
    }
    const strategyWeight = strategyConfigurations[tab]?.weight || 0
    return `${strategyWeight}%`
  }

  return (
    <Box gap="s12" width="full">
      {!showEmptyState && (
        <Text.Normal weight="semibold">
          {i18n.t('performance_process.reviews.templates.questions.title')}
        </Text.Normal>
      )}
      <Box flexDirection="row" gap="s12">
        {tabs.map((tab) => {
          const tag = getTabTag(tab.id)
          return (
            <SimpleChip
              key={tab.id}
              label={tag ? `${tab.label} - ${tag} ` : tab.label}
              active={tab.id === currentTab}
              disabled={false}
              onClick={() => handleChipClick(tab.id)}
            />
          )
        })}
      </Box>

      <Box paddingY="s16" gap="s32">
        {showEmptyState ? (
          <EmptyState
            title={i18n.t('performance_process.reviews.templates.questions.empty_state.title')}
            description={i18n.t(
              'performance_process.reviews.templates.questions.empty_state.description'
            )}
            illustration="page-edit"
          />
        ) : (
          <>
            <Questionnaire
              translations={getQuestionnaireTranslations()}
              content={questionnaireContent}
              key={currentTab}
              renderSectionHeaderAddon={renderSectionHeader}
            />
            <Questionnaire
              translations={getQuestionnaireTranslations()}
              content={competenciesPlaceholderContent}
              renderSectionHeaderAddon={renderSectionHeader}
            />

            <Questionnaire
              translations={getQuestionnaireTranslations()}
              content={employeeScoreContent}
              renderSectionHeaderAddon={renderSectionHeader}
            />
            <Questionnaire
              translations={getQuestionnaireTranslations()}
              content={employeePotentialScoreContent}
            />
          </>
        )}
      </Box>
    </Box>
  )
}
